import React, {
    createContext,
    useContext,
    useReducer,
    useEffect,
    useMemo,
    useCallback,
} from 'react';
import {
    SubscriptionPlanResponse,
    SubscriptionResponse,
    SubscriptionStatus,
} from 'shared/types/Subscription';
import { isSubscriptionActive } from 'shared/utils/isSubscriptionActive';

import { SEATS_LIMIT_FOR_NON_PREMIUM } from '@/hooks/useReachedTeamSeatsLimit';
import { useSubscriptionsPlans } from '@/hooks/useSubscriptionPlans';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import Logger from '@/services/logger';

import { useOrganisationsContext } from './OrganisationsContext';

interface ExtendedSubscriptionPlan extends Partial<SubscriptionPlanResponse> {
    seats: number;
}

interface SubscriptionState {
    loading: boolean;
    userSubscriptions: SubscriptionResponse[];
    currentOrganisationSubscription: SubscriptionResponse | null;
    currentOrganisationSubscriptionPlan: ExtendedSubscriptionPlan | null;
    isCurrentSubscriptionActive: boolean;
    isCurrentSubscriptionInternalTrial: boolean;
}

type SubscriptionAction =
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_SUBSCRIPTION_DATA'; payload: Partial<SubscriptionState> }
    | { type: 'RESET_STATE' }
    | {
          type: 'UPDATE_SUBSCRIPTION_STATE';
          payload: {
              subscription: SubscriptionResponse | null;
              plan: ExtendedSubscriptionPlan | null;
              isInternalTrial: boolean;
              subscriptions: SubscriptionResponse[];
          };
      };

const initialState: SubscriptionState = {
    loading: true,
    userSubscriptions: [],
    currentOrganisationSubscription: null,
    currentOrganisationSubscriptionPlan: null,
    isCurrentSubscriptionActive: false,
    isCurrentSubscriptionInternalTrial: false,
};

const subscriptionReducer = (
    state: SubscriptionState,
    action: SubscriptionAction,
): SubscriptionState => {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };

        case 'SET_SUBSCRIPTION_DATA':
            return { ...state, ...action.payload };

        case 'UPDATE_SUBSCRIPTION_STATE':
            return {
                ...state,
                currentOrganisationSubscription: action.payload.subscription,
                currentOrganisationSubscriptionPlan: action.payload.plan,
                userSubscriptions: action.payload.subscriptions,
                isCurrentSubscriptionActive: isSubscriptionActive(
                    action.payload.subscription,
                ),
                isCurrentSubscriptionInternalTrial:
                    action.payload.isInternalTrial,
                loading: false,
            };

        case 'RESET_STATE':
            return initialState;

        default:
            return state;
    }
};

interface SubscriptionContextValue extends SubscriptionState {
    webPlans: {
        premium: null | SubscriptionPlanResponse;
        teams: null | SubscriptionPlanResponse;
    };
    allPlans: SubscriptionPlanResponse[];
}

const defaultContextValue: SubscriptionContextValue = {
    ...initialState,
    webPlans: { premium: null, teams: null },
    allPlans: [],
};

const SubscriptionContext =
    createContext<SubscriptionContextValue>(defaultContextValue);

interface SubscriptionProviderProps {
    children: React.ReactNode;
}

const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
    children,
}) => {
    const { userOrganisation } = useOrganisationsContext();

    const [state, dispatch] = useReducer(subscriptionReducer, initialState);

    const { allPlans, webPlans } = useSubscriptionsPlans();
    const { subscriptions } = useSubscriptions();

    const findCurrentSubscription = useCallback(
        (
            subscriptions: SubscriptionResponse[],
            organisationId: string,
        ): SubscriptionResponse | null => {
            return (
                subscriptions.find(
                    (sub) => sub.organisation_id === organisationId,
                ) ?? null
            );
        },
        [],
    );

    const checkInternalTrialStatus = useCallback(
        (subscription: SubscriptionResponse | null): boolean => {
            return subscription?.status === SubscriptionStatus.INTERNAL_TRIAL;
        },
        [],
    );

    const getSubscriptionPlan = useCallback(
        (
            subscription: SubscriptionResponse | null,
            isInternalTrial: boolean,
            plans: SubscriptionPlanResponse[],
        ): ExtendedSubscriptionPlan | null => {
            if (isInternalTrial) {
                return { seats: SEATS_LIMIT_FOR_NON_PREMIUM };
            }

            if (!subscription?.product_id) {
                return null;
            }

            const plan = plans.find(
                (plan) => plan.product_id === subscription.product_id,
            );

            return plan ? { ...plan, seats: plan.seats } : null;
        },
        [],
    );

    const updateSubscriptionState = useCallback(
        (
            subscription: SubscriptionResponse | null,
            plan: ExtendedSubscriptionPlan | null,
            isInternalTrial: boolean,
        ) => {
            dispatch({
                type: 'UPDATE_SUBSCRIPTION_STATE',
                payload: {
                    subscription,
                    plan,
                    isInternalTrial,
                    subscriptions: subscriptions ?? [],
                },
            });
        },
        [subscriptions],
    );

    useEffect(() => {
        if (!subscriptions || !userOrganisation || !allPlans.length) {
            return;
        }

        try {
            const currentSubscription = findCurrentSubscription(
                subscriptions,
                userOrganisation.id,
            );

            const isInternalTrial =
                checkInternalTrialStatus(currentSubscription);

            const currentSubscribedPlan = getSubscriptionPlan(
                currentSubscription,
                isInternalTrial,
                allPlans,
            );

            if (
                currentSubscription &&
                !currentSubscribedPlan &&
                !isInternalTrial
            ) {
                Logger.logError(
                    `Subscription plan not found for subscription with product_id: ${currentSubscription.product_id}`,
                );
            }

            updateSubscriptionState(
                currentSubscription,
                currentSubscribedPlan,
                isInternalTrial,
            );
        } catch (error) {
            Logger.logError(`Error updating subscription state: ${error}`);

            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [
        subscriptions,
        userOrganisation,
        allPlans,
        findCurrentSubscription,
        checkInternalTrialStatus,
        getSubscriptionPlan,
        updateSubscriptionState,
    ]);

    const contextValue = useMemo<SubscriptionContextValue>(
        () => ({
            ...state,
            allPlans,
            webPlans,
        }),
        [state, allPlans, webPlans],
    );
    return (
        <SubscriptionContext.Provider value={contextValue}>
            {children}
        </SubscriptionContext.Provider>
    );
};

function useSubscriptionContext(): SubscriptionContextValue {
    const context = useContext(SubscriptionContext);

    if (context === undefined) {
        throw new Error(
            'useSubscriptionContext must be used within a SubscriptionProvider',
        );
    }

    return context;
}

export { SubscriptionProvider, useSubscriptionContext };

export type {
    SubscriptionContextValue,
    ExtendedSubscriptionPlan,
    SubscriptionState,
    SubscriptionAction,
};
